import React, {
    useState,
    useRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    forwardRef,
} from 'react';
import {
    Paper,
    List,
    ListItemButton,
    ListItemAvatar,
    ListItemText,
    Popper,
    InputLabel,
    FormControl,
    OutlinedInput,
    IconButton,
    TextField,
    Stack,
    ClickAwayListener,
} from '@mui/material';
import { Link as LinkIcon, Check as CheckIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { grey, red } from '@mui/material/colors';
import { useCache } from '../api/cache/Cache';
import { useAuth } from '../auth/Auth';
import { UserSummary } from '../database/user';
import Avatar from '../profile/info/Avatar';

export interface MentionInputHandle {
    /** Returns the value with mentions serialized as @[Display Name](username) */
    getSerializedValue: () => string;
}

export interface MentionInputProps {
    value: string;
    onChange: (value: string) => void;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    fullWidth?: boolean;
    multiline?: boolean;
    minRows?: number;
    maxRows?: number;
    size?: 'small' | 'medium';
    variant?: 'outlined' | 'filled' | 'standard';
    margin?: 'none' | 'dense' | 'normal';
    /** Extra sx for the outer FormControl */
    sx?: Record<string, any>;
    inputProps?: Record<string, any>;
    InputLabelProps?: Record<string, any>;
}

/**
 * Populates a contentEditable element from the serialized storage format
 * (@[Display Name](username) for mentions, %[text](url) for links).
 * Called once on mount when an initial value is provided (e.g. edit dialogs).
 */
function buildInitialDOM(
    el: HTMLElement,
    serialized: string,
    mentionBg: string,
    linkColor: string,
): void {
    el.innerHTML = '';
    const combined = /(@\[([^\]]*)\]\(([^)]*)\))|(%\[([^\]]*)\]\(([^)]*)\))/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    const appendText = (text: string) => {
        const parts = text.split('\n');
        parts.forEach((part, i) => {
            if (part) el.appendChild(document.createTextNode(part));
            if (i < parts.length - 1) el.appendChild(document.createElement('br'));
        });
    };

    while ((match = combined.exec(serialized)) !== null) {
        if (match.index > lastIndex) {
            appendText(serialized.slice(lastIndex, match.index));
        }
        if (match[1]) {
            const span = document.createElement('span');
            span.contentEditable = 'false';
            span.dataset.username = match[3];
            span.textContent = `@${match[2]}`;
            span.style.fontWeight = '700';
            span.style.backgroundColor = mentionBg;
            span.style.borderRadius = '4px';
            span.style.padding = '0 2px';
            el.appendChild(span);
        } else if (match[4]) {
            const span = document.createElement('span');
            span.dataset.url = match[6];
            span.textContent = match[5];
            span.style.color = linkColor;
            span.style.textDecoration = 'underline';
            span.style.cursor = 'pointer';
            el.appendChild(span);
        }
        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < serialized.length) {
        appendText(serialized.slice(lastIndex));
    }
}

/**
 * Reads the contents of a contentEditable element and returns:
 * - plainText: what the user sees (e.g. "Hey @Dain Kasprak!")
 * - serialized: storage format (e.g. "Hey @[Dain Kasprak](google_123)!")
 */
function readEditable(el: HTMLElement): { plainText: string; serialized: string } {
    let plain = '';
    let serialized = '';

    el.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            plain += node.textContent ?? '';
            serialized += node.textContent ?? '';
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            if (element.dataset.username) {
                const displayName = element.textContent?.replace(/^@/, '') ?? '';
                plain += `@${displayName}`;
                serialized += `@[${displayName}](${element.dataset.username})`;
            } else if (element.dataset.url) {
                const text = element.textContent ?? '';
                plain += text;
                serialized += `%[${text}](${element.dataset.url})`;
            } else if (element.tagName === 'BR') {
                plain += '\n';
                serialized += '\n';
            } else if (element.tagName === 'DIV' || element.tagName === 'P') {
                const inner = readEditable(element);
                if (plain.length > 0 && !plain.endsWith('\n')) {
                    plain += '\n';
                    serialized += '\n';
                }
                plain += inner.plainText;
                serialized += inner.serialized;
            } else {
                plain += element.textContent ?? '';
                serialized += element.textContent ?? '';
            }
        }
    });

    return { plainText: plain, serialized };
}

/**
 * Returns the plain text before the cursor inside a contentEditable element.
 */
function getTextBeforeCursor(el: HTMLElement): string | null {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    const preRange = document.createRange();
    preRange.selectNodeContents(el);
    preRange.setEnd(range.startContainer, range.startOffset);
    const fragment = preRange.cloneContents();
    const div = document.createElement('div');
    div.appendChild(fragment);
    return div.textContent ?? '';
}

/**
 * Custom input component for OutlinedInput that renders a contentEditable div.
 * Defined outside MentionInput to avoid remounting on every render.
 */
const EditableDiv = forwardRef<HTMLDivElement, any>((props, ref) => {
    // Strip MUI-internal props that shouldn't go on a DOM element
    const { ownerState, ...domProps } = props;
    return <div ref={ref} {...domProps} />;
});
EditableDiv.displayName = 'EditableDiv';

/**
 * MentionInput is a contentEditable-based input wrapped in MUI's OutlinedInput
 * that shows an autocomplete dropdown when the user types '@'. Selecting a user
 * inserts a styled mention span. Call getSerializedValue() via ref to get
 * the storage format.
 */
const MentionInput = forwardRef<MentionInputHandle, MentionInputProps>(
    (
        {
            value,
            onChange,
            label,
            placeholder,
            disabled = false,
            fullWidth = false,
            multiline = false,
            minRows,
            maxRows,
            size = 'medium',
            margin = 'none',
            sx,
            inputProps: inputPropsProp,
            InputLabelProps,
        },
        ref,
    ) => {
        const theme = useTheme();
        const cache = useCache();
        const auth = useAuth();
        const editableRef = useRef<HTMLDivElement>(null);
        const anchorRef = useRef<HTMLDivElement>(null);

        const [mentionStartIndex, setMentionStartIndex] = useState<number | null>(null);
        const [showSuggestions, setShowSuggestions] = useState(false);
        const [suggestions, setSuggestions] = useState<UserSummary[]>([]);
        const [selectedIndex, setSelectedIndex] = useState(0);
        const [focused, setFocused] = useState(false);

        // Link insertion state
        const [showLinkInput, setShowLinkInput] = useState(false);
        const [linkUrl, setLinkUrl] = useState('');
        const [hasSelection, setHasSelection] = useState(false);
        const savedSelectionRef = useRef<Range | null>(null);
        const linkButtonRef = useRef<HTMLButtonElement>(null);
        const linkUrlInputRef = useRef<HTMLInputElement>(null);
        // Track when the user is interacting with the link button / popover
        // so we don't set focused=false and hide the UI on mobile.
        const interactingRef = useRef(false);

        // Focus the URL input without scrolling when the popover opens
        useEffect(() => {
            if (showLinkInput) {
                // Small delay to let the Popper render/position first
                requestAnimationFrame(() => {
                    linkUrlInputRef.current?.focus({ preventScroll: true });
                });
            }
        }, [showLinkInput]);

        useImperativeHandle(ref, () => ({
            getSerializedValue: () => {
                if (!editableRef.current) return value;
                return readEditable(editableRef.current).serialized;
            },
        }));

        // Initialize contentEditable once on mount if a non-empty initial value is
        // provided (e.g. when opening an edit dialog pre-populated with saved content).
        const initRef = useRef({
            value,
            mentionBg:
                theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.1)'
                    : 'rgba(0,0,0,0.08)',
            linkColor: theme.palette.primary.main,
        });
        useEffect(() => {
            const el = editableRef.current;
            const { value: initialValue, mentionBg, linkColor } = initRef.current;
            if (el && initialValue) {
                buildInitialDOM(el, initialValue, mentionBg, linkColor);
            }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        // Sync external value → contentEditable only when the value is cleared
        // (e.g. after submit). We don't overwrite on every render to preserve
        // cursor position and mention spans.
        const prevValueRef = useRef(value);
        useEffect(() => {
            if (value === '' && prevValueRef.current !== '' && editableRef.current) {
                editableRef.current.innerHTML = '';
            }
            prevValueRef.current = value;
        }, [value]);

        // Keep a .value property on the div so MUI's isFilled() check works
        useEffect(() => {
            if (editableRef.current) {
                (editableRef.current as any).value = value;
            }
        }, [value]);

        const closeSuggestions = useCallback(() => {
            setShowSuggestions(false);
            setMentionStartIndex(null);
            setSuggestions([]);
            setSelectedIndex(0);
        }, []);

        // Track text selection inside the editable area
        const checkSelection = useCallback(() => {
            const el = editableRef.current;
            if (!el) return;
            const sel = window.getSelection();
            if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
                setHasSelection(false);
                return;
            }
            // Only care about selections within our editable
            if (el.contains(sel.anchorNode) && el.contains(sel.focusNode)) {
                setHasSelection(true);
            } else {
                setHasSelection(false);
            }
        }, []);

        useEffect(() => {
            document.addEventListener('selectionchange', checkSelection);
            return () => document.removeEventListener('selectionchange', checkSelection);
        }, [checkSelection]);

        const openLinkInput = useCallback(() => {
            const sel = window.getSelection();
            if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
            savedSelectionRef.current = sel.getRangeAt(0).cloneRange();

            // Check if the selection is inside or wraps an existing link span
            let existingUrl = '';

            // Case 1: Anchor node is inside / is a link span
            const node = sel.anchorNode;
            const parent =
                node?.nodeType === Node.ELEMENT_NODE
                    ? (node as HTMLElement)
                    : node?.parentElement;
            const linkSpan = parent?.closest?.('[data-url]') as HTMLElement | null;
            if (linkSpan?.dataset.url) {
                existingUrl = linkSpan.dataset.url;
            }

            // Case 2: Selection wraps a link span (contentEditable=false spans
            // are selected as a whole, so check the range contents)
            if (!existingUrl) {
                const range = sel.getRangeAt(0);
                const fragment = range.cloneContents();
                const linkInFragment = fragment.querySelector?.(
                    '[data-url]',
                ) as HTMLElement | null;
                if (linkInFragment?.dataset.url) {
                    existingUrl = linkInFragment.dataset.url;
                }
            }

            setLinkUrl(existingUrl);
            setShowLinkInput(true);
        }, []);

        const closeLinkInput = useCallback(() => {
            setShowLinkInput(false);
            setLinkUrl('');
            savedSelectionRef.current = null;
        }, []);

        const removeLink = useCallback(() => {
            const el = editableRef.current;
            const range = savedSelectionRef.current;
            if (!el || !range) return;

            // Find link span(s) in or around the saved selection
            const findLinkSpan = (): HTMLElement | null => {
                // Check anchor node's parent chain
                const node = range.startContainer;
                const parent =
                    node.nodeType === Node.ELEMENT_NODE
                        ? (node as HTMLElement)
                        : node.parentElement;
                const span = parent?.closest?.('[data-url]') as HTMLElement | null;
                if (span && el.contains(span)) return span;

                // Check if range wraps a link span
                const walker = document.createTreeWalker(el, NodeFilter.SHOW_ELEMENT, {
                    acceptNode: (n) =>
                        (n as HTMLElement).dataset.url && range.intersectsNode(n)
                            ? NodeFilter.FILTER_ACCEPT
                            : NodeFilter.FILTER_SKIP,
                });
                return walker.nextNode() as HTMLElement | null;
            };

            const linkSpan = findLinkSpan();
            if (!linkSpan) {
                closeLinkInput();
                return;
            }

            // Replace the link span with its plain text content
            const textNode = document.createTextNode(linkSpan.textContent ?? '');
            const textLength = textNode.length;
            linkSpan.parentNode?.replaceChild(textNode, linkSpan);

            // Remember position before normalize (which may merge textNode into a sibling)
            const cursorNode = textNode.previousSibling;
            const offsetBeforeMerge = cursorNode
                ? (cursorNode.textContent?.length ?? 0)
                : 0;

            // Normalize to merge adjacent text nodes
            el.normalize();

            // Place cursor at end of the replaced text
            const sel = window.getSelection();
            if (sel) {
                const newRange = document.createRange();
                // After normalize, textNode may have been merged. Find the right position.
                if (textNode.parentNode) {
                    // textNode survived normalize
                    newRange.setStartAfter(textNode);
                } else if (cursorNode && cursorNode.parentNode) {
                    // textNode was merged into cursorNode (the preceding sibling)
                    newRange.setStart(cursorNode, offsetBeforeMerge + textLength);
                } else {
                    // Fallback: place cursor at end of editable
                    newRange.selectNodeContents(el);
                    newRange.collapse(false);
                }
                newRange.collapse(true);
                sel.removeAllRanges();
                sel.addRange(newRange);

                // Clear residual inline styles the browser carries forward from
                // the removed link span (color, underline) so new typing is clean.
                document.execCommand('removeFormat', false);
            }

            const { plainText } = readEditable(el);
            onChange(plainText);
            closeLinkInput();
        }, [onChange, closeLinkInput]);

        const insertLink = useCallback(() => {
            const el = editableRef.current;
            const range = savedSelectionRef.current;
            if (!el || !range) return;

            let url = linkUrl.trim();
            // If URL is cleared, remove the link instead
            if (!url) {
                removeLink();
                return;
            }
            // Auto-add https:// if no protocol is present
            if (!/^https?:\/\//i.test(url)) {
                url = `https://${url}`;
            }

            // Restore the saved selection
            const sel = window.getSelection();
            if (!sel) return;
            sel.removeAllRanges();
            sel.addRange(range);

            const selectedText = range.toString();
            if (!selectedText) return;

            // Delete the selected text
            range.deleteContents();

            // Create link span
            const span = document.createElement('span');
            span.dataset.url = url;
            span.textContent = selectedText;
            span.style.color = theme.palette.primary.main;
            span.style.textDecoration = 'underline';
            span.style.cursor = 'pointer';

            range.insertNode(span);

            // Move cursor after the link
            const space = document.createTextNode('\u00A0');
            span.after(space);
            const newRange = document.createRange();
            newRange.setStartAfter(space);
            newRange.collapse(true);
            sel.removeAllRanges();
            sel.addRange(newRange);

            const { plainText } = readEditable(el);
            onChange(plainText);
            closeLinkInput();
        }, [linkUrl, onChange, closeLinkInput, removeLink, theme.palette.primary.main]);

        const handleInput = useCallback(() => {
            const el = editableRef.current;
            if (!el) return;

            // Clean up orphaned styled elements the browser creates when
            // text adjacent to (or replacing) a removed link span is typed.
            // These are <span>, <font>, <u>, <b>, <i>, etc. that carry
            // leftover color / underline but have no data-url or
            // data-username attribute.
            const orphans = el.querySelectorAll(
                'span:not([data-url]):not([data-username]), font, u, b, i, em, strong, s, strike',
            );
            if (orphans.length > 0) {
                // Save cursor position before mutation
                const sel = window.getSelection();
                let savedOffset = 0;
                if (sel && sel.rangeCount > 0) {
                    // Count total text length up to the cursor
                    const r = sel.getRangeAt(0);
                    const preRange = document.createRange();
                    preRange.selectNodeContents(el);
                    preRange.setEnd(r.startContainer, r.startOffset);
                    savedOffset = preRange.toString().length;
                }

                orphans.forEach((node) => {
                    const parent = node.parentNode;
                    if (!parent) return;
                    while (node.firstChild) {
                        parent.insertBefore(node.firstChild, node);
                    }
                    parent.removeChild(node);
                });
                el.normalize();

                // Restore cursor position
                if (sel) {
                    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
                    let charCount = 0;
                    let textNode: Text | null = null;
                    while (walker.nextNode()) {
                        textNode = walker.currentNode as Text;
                        if (charCount + textNode.length >= savedOffset) {
                            const newRange = document.createRange();
                            newRange.setStart(textNode, savedOffset - charCount);
                            newRange.collapse(true);
                            sel.removeAllRanges();
                            sel.addRange(newRange);
                            break;
                        }
                        charCount += textNode.length;
                    }
                }
            }

            const { plainText } = readEditable(el);
            onChange(plainText);

            const textBefore = getTextBeforeCursor(el);
            if (textBefore === null) {
                closeSuggestions();
                return;
            }

            const lastAtIndex = textBefore.lastIndexOf('@');
            if (lastAtIndex === -1) {
                closeSuggestions();
                return;
            }

            if (lastAtIndex > 0 && !/\s|\n/.test(textBefore[lastAtIndex - 1])) {
                closeSuggestions();
                return;
            }

            const query = textBefore.slice(lastAtIndex + 1);
            if (query.length > 30) {
                closeSuggestions();
                return;
            }

            setMentionStartIndex(lastAtIndex);

            const allUsers = cache.userSummaries.list();
            const currentUsername = auth.user?.username;
            const filtered = allUsers.filter(
                (user) =>
                    user.username !== currentUsername &&
                    user.displayName.toLowerCase().includes(query.toLowerCase()),
            );
            setSuggestions(filtered.slice(0, 6));
            setShowSuggestions(filtered.length > 0);
            setSelectedIndex(0);
        }, [onChange, cache.userSummaries, closeSuggestions]);

        const insertMention = useCallback(
            (user: UserSummary) => {
                const el = editableRef.current;
                if (!el || mentionStartIndex === null) return;

                const sel = window.getSelection();
                if (!sel || sel.rangeCount === 0) return;

                // Find and delete text from @ to cursor
                const textBefore = getTextBeforeCursor(el);
                if (textBefore === null) return;

                const deleteCount = textBefore.length - mentionStartIndex;

                // Delete chars backwards from cursor
                for (let i = 0; i < deleteCount; i++) {
                    const r = sel.getRangeAt(0);
                    r.setStart(r.startContainer, Math.max(0, r.startOffset - 1));
                    r.deleteContents();
                }

                // Create mention span
                const span = document.createElement('span');
                span.contentEditable = 'false';
                span.dataset.username = user.username;
                span.textContent = `@${user.displayName}`;
                span.style.fontWeight = '700';
                span.style.backgroundColor =
                    theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.1)'
                        : 'rgba(0,0,0,0.08)';
                span.style.borderRadius = '4px';
                span.style.padding = '0 2px';

                // Insert span + trailing space
                const currentRange = sel.getRangeAt(0);
                currentRange.insertNode(span);
                const space = document.createTextNode('\u00A0');
                span.after(space);

                // Move cursor after the space
                const newRange = document.createRange();
                newRange.setStartAfter(space);
                newRange.collapse(true);
                sel.removeAllRanges();
                sel.addRange(newRange);

                const { plainText } = readEditable(el);
                onChange(plainText);
                closeSuggestions();
            },
            [mentionStartIndex, onChange, closeSuggestions, theme.palette.mode],
        );

        const handleKeyDown = useCallback(
            (e: React.KeyboardEvent) => {
                // Ctrl/Cmd+K to insert link
                if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    const sel = window.getSelection();
                    if (sel && !sel.isCollapsed) {
                        openLinkInput();
                    }
                    return;
                }

                if (!showSuggestions || suggestions.length === 0) return;

                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSelectedIndex((prev) => (prev + 1) % suggestions.length);
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSelectedIndex(
                        (prev) => (prev - 1 + suggestions.length) % suggestions.length,
                    );
                } else if (e.key === 'Enter') {
                    e.preventDefault();
                    insertMention(suggestions[selectedIndex]);
                } else if (e.key === 'Escape') {
                    closeSuggestions();
                }
            },
            [
                showSuggestions,
                suggestions,
                selectedIndex,
                insertMention,
                closeSuggestions,
                openLinkInput,
            ],
        );

        useEffect(() => {
            const handleClickOutside = () => {
                if (showSuggestions) closeSuggestions();
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, [showSuggestions, closeSuggestions]);

        const hasContent = value.length > 0;

        return (
            <FormControl
                fullWidth={fullWidth}
                variant='outlined'
                size={size}
                margin={margin}
                sx={{ position: 'relative', ...sx }}
                ref={anchorRef}
            >
                {label && (
                    <InputLabel
                        shrink={focused || hasContent}
                        sx={{
                            color: grey[50],
                            '&.Mui-focused': { color: grey[50] },
                        }}
                        {...InputLabelProps}
                    >
                        {label}
                    </InputLabel>
                )}
                <OutlinedInput
                    inputComponent={EditableDiv as any}
                    inputRef={editableRef}
                    inputProps={{
                        contentEditable: !disabled,
                        suppressContentEditableWarning: true,
                        onInput: handleInput,
                        onKeyDown: handleKeyDown,
                        onPaste: (e: React.ClipboardEvent) => {
                            e.preventDefault();
                            const text = e.clipboardData.getData('text/plain');
                            document.execCommand('insertText', false, text);
                        },
                        'data-placeholder': placeholder ?? '',
                        style: {
                            outline: 'none',
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word' as const,
                            minHeight: minRows ? `${minRows * 1.4375}em` : undefined,
                            maxHeight: maxRows ? `${maxRows * 1.4375}em` : '150px',
                            overflowY: 'auto' as const,
                            ...inputPropsProp?.style,
                        },
                    }}
                    multiline
                    label={focused || hasContent ? label : undefined}
                    notched={focused || hasContent}
                    onFocus={() => setFocused(true)}
                    onBlur={() => {
                        // On mobile, tapping the link button fires blur before
                        // the button's pointer event. Delay to let interactingRef
                        // be set first.
                        setTimeout(() => {
                            if (!interactingRef.current) {
                                setFocused(false);
                            }
                        }, 0);
                    }}
                    disabled={disabled}
                    sx={{
                        cursor: disabled ? 'default' : 'text',
                        color: grey[50],
                        '& fieldset': {
                            borderColor: grey[50],
                        },
                        '&:hover fieldset': {
                            borderColor: red[900],
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: red[900],
                        },
                        '&.Mui-focused .MuiInputBase-input:empty::before': placeholder
                            ? {
                                  content: `"${placeholder}"`,
                                  color: 'rgba(255,255,255,0.5)',
                                  pointerEvents: 'none',
                              }
                            : undefined,
                    }}
                    onClick={() => {
                        if (!disabled) editableRef.current?.focus();
                    }}
                />

                {/* Link button – visible when text is selected */}
                {focused && hasSelection && !showLinkInput && (
                    <IconButton
                        ref={linkButtonRef}
                        size='small'
                        onPointerDown={(e) => {
                            // Prevent the tap/click from stealing focus / collapsing selection
                            e.preventDefault();
                            e.stopPropagation();
                            interactingRef.current = true;
                            openLinkInput();
                            // Re-focus the editable so focused state stays true
                            editableRef.current?.focus();
                            setTimeout(() => {
                                interactingRef.current = false;
                            }, 100);
                        }}
                        sx={{
                            position: 'absolute',
                            right: 4,
                            top: 4,
                            zIndex: 1,
                            bgcolor:
                                theme.palette.mode === 'dark'
                                    ? 'rgba(255,255,255,0.08)'
                                    : 'rgba(0,0,0,0.06)',
                            '&:hover': {
                                bgcolor:
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.16)'
                                        : 'rgba(0,0,0,0.12)',
                            },
                        }}
                        title='Insert link (Ctrl+K)'
                    >
                        <LinkIcon
                            fontSize='small'
                            sx={{ color: theme.palette.secondary.main }}
                        />
                    </IconButton>
                )}

                {/* Link URL input popover */}
                <Popper
                    open={showLinkInput}
                    anchorEl={anchorRef.current}
                    placement='bottom-start'
                    style={{
                        zIndex: theme.zIndex.modal + 1,
                    }}
                    modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
                >
                    <ClickAwayListener onClickAway={closeLinkInput}>
                        <Paper
                            elevation={8}
                            onPointerDown={() => {
                                interactingRef.current = true;
                            }}
                            onPointerUp={() => {
                                setTimeout(() => {
                                    interactingRef.current = false;
                                }, 100);
                            }}
                            sx={{
                                p: 1,
                                border: `1px solid ${theme.palette.divider}`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            <LinkIcon
                                fontSize='small'
                                sx={{ color: grey[400], mr: 0.5 }}
                            />
                            <TextField
                                inputRef={linkUrlInputRef}
                                size='small'
                                placeholder='Paste or type URL...'
                                value={linkUrl}
                                onChange={(e) => setLinkUrl(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        insertLink();
                                    } else if (e.key === 'Escape') {
                                        closeLinkInput();
                                    }
                                }}
                                sx={{
                                    '& .MuiInputBase-root': { height: 32 },
                                    '& .MuiInputBase-input': { fontSize: 13, py: 0.5 },
                                    minWidth: 220,
                                }}
                            />
                            <IconButton
                                size='small'
                                onClick={insertLink}
                                color='primary'
                                title={linkUrl.trim() ? 'Apply link' : 'Remove link'}
                            >
                                <CheckIcon fontSize='small' />
                            </IconButton>
                        </Paper>
                    </ClickAwayListener>
                </Popper>

                <Popper
                    open={showSuggestions && suggestions.length > 0}
                    anchorEl={anchorRef.current}
                    placement='bottom-start'
                    style={{
                        zIndex: theme.zIndex.modal + 1,
                        width: anchorRef.current?.offsetWidth,
                    }}
                    modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
                >
                    <Paper
                        elevation={8}
                        sx={{
                            maxHeight: 250,
                            overflow: 'auto',
                            border: `1px solid ${theme.palette.divider}`,
                        }}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <List dense disablePadding>
                            {suggestions.map((user, i) => (
                                <ListItemButton
                                    key={user.username}
                                    selected={i === selectedIndex}
                                    onClick={() => insertMention(user)}
                                >
                                    <ListItemAvatar>
                                        <Avatar
                                            username={user.username}
                                            displayName={user.displayName}
                                            size={32}
                                        />
                                    </ListItemAvatar>
                                    <ListItemText primary={user.displayName} />
                                </ListItemButton>
                            ))}
                        </List>
                    </Paper>
                </Popper>
            </FormControl>
        );
    },
);

MentionInput.displayName = 'MentionInput';

export default MentionInput;
